import { EditType } from './EditType';
import { ItemActionType } from './SetMarketplaceAction';
import DropConfirm from './DropConfirm';
import DropItem from './DropItem';
import {
  SvgAccount,
  SvgParcel,
  SvgProtectionAdd,
  SvgArrowLeftCircle,
  SvgDeleteBin,
  P2GDivider,
  SvgQuotes,
  SvgCardPayment,
  SvgRuleWand,
  P2GGrid,
  SvgSignature,
  P2GButton
} from '@parcel2go/core-ui';
import { EventSections } from '../../../lib/events/EventLogger';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { ClickableComponent } from './ClickableComponent';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import useOnLoad from '~/routes/use-on-load';
import { ToSmartSendAnalyticsModel } from '../quotes/utils/CurrentSelectionType';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';

interface props {
  edit: (type: EditType) => void;
  action: (type: ItemActionType) => void;
  saveForLater: (select: boolean) => void;
  deleteItem: () => void;
  book: () => void;
  canBook: boolean;
  direction: 'row' | 'column';
  isXs?: boolean;
  item: SmartSendItemApiModel;
}

export default function SmartSendItemActiveDrop(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const linkVariant = 'body2';
  const { track } = useSmartSendAnalytics();

  useOnLoad(() => {
    track({
      type: 'quick_actions_loaded',
      payload: {
        ...GenerateBaseAnalyticsPayload([
          EventSections.SmartSend,
          EventSections.SmartSendItem,
          EventSections.QuickActions
        ])
      }
    });
  });

  const onClick = (link: string) => {
    track({
      type: 'quick_actions_click',
      payload: {
        ...GenerateBaseAnalyticsPayload([
          EventSections.SmartSend,
          EventSections.SmartSendItem,
          EventSections.QuickActions
        ]),
        action: link,
        items: [ToSmartSendAnalyticsModel(props.item)]
      }
    });
  };

  const component = props.isXs ? 'button' : 'link';

  return (
    <div style={{ width: props.isXs ? '100%' : props.direction === 'row' ? '100%' : '200px' }}>
      <P2GGrid
        container
        gap={props.isXs ? 2 : 1}
        justify={props.direction === 'row' ? 'center' : 'flex-start'}
        direction={props.direction || 'column'}
      >
        <DropItem icon={<SvgAccount />} iconSrc="/theme/icons/Account.svg">
          <ClickableComponent
            fullWidth={props.isXs}
            component={component}
            linkVariant={linkVariant}
            onClick={() => {
              onClick('address');
              props.edit('ADDRESS');
            }}
          >
            {t('EDIT_ADDRESS_LINK', { defaultValue: 'Edit Address' })}
          </ClickableComponent>
        </DropItem>
        {SettingsCache.settings.Features.includes('InlineRules') && (
          <DropItem icon={<SvgRuleWand />} iconSrc="/theme/icons/RuleWand.svg">
            <ClickableComponent
              fullWidth={props.isXs}
              component={component}
              linkVariant={linkVariant}
              onClick={() => {
                onClick('rules');
                props.edit('RULES');
              }}
            >
              {t('EDIT_RULES_LINK', { defaultValue: 'Edit Rules' })}
            </ClickableComponent>
          </DropItem>
        )}
        <DropItem icon={<SvgParcel />} iconSrc="/theme/icons/Parcel.svg">
          <ClickableComponent
            fullWidth={props.isXs}
            component={component}
            linkVariant={linkVariant}
            onClick={() => {
              onClick('contents');
              props.edit('CONTENTS');
            }}
          >
            {t('EDIT_CONTENTS_LINK', { defaultValue: 'Edit Contents' })}
          </ClickableComponent>
        </DropItem>
        {SettingsCache.settings.Features.includes('Extras') && (
          <DropItem icon={<SvgSignature />} iconSrc="/theme/icons/Signature.svg">
            <ClickableComponent
              fullWidth={props.isXs}
              component={component}
              linkVariant={linkVariant}
              onClick={() => {
                onClick('extras');
                props.edit('EXTRAS');
              }}
            >
              {t('CHOOSE_EXTRAS_LINK', { defaultValue: 'Choose Extras' })}
            </ClickableComponent>
          </DropItem>
        )}
        {SettingsCache.settings.Features.includes('Protection') && (
          <DropItem icon={<SvgProtectionAdd />} iconSrc="/theme/icons/ProtectionAdd.svg">
            <ClickableComponent
              fullWidth={props.isXs}
              component={component}
              linkVariant={linkVariant}
              onClick={() => {
                onClick('protection');
                props.edit('EXTRAS');
              }}
            >
              {t('ADD_PROTECTION_LINK', { defaultValue: 'Add Protection' })}
            </ClickableComponent>
          </DropItem>
        )}
        <DropItem icon={<SvgQuotes />} iconSrc="/theme/icons/Quotes.svg">
          <ClickableComponent
            component={component}
            fullWidth={props.isXs}
            linkVariant={linkVariant}
            onClick={() => {
              onClick('service');
              props.edit('SERVICE');
            }}
          >
            {t('CHOOSE_SERVICE_LINK', { defaultValue: 'Choose Service' })}
          </ClickableComponent>
        </DropItem>
        <P2GGrid item>
          <P2GDivider />
        </P2GGrid>
        <DropItem icon={<SvgArrowLeftCircle />} iconSrc="/theme/icons/ArrowLeftCircle.svg">
          <ClickableComponent
            component={component}
            fullWidth={props.isXs}
            linkVariant={linkVariant}
            onClick={() => {
              onClick('saveforlater');
              props.saveForLater(false);
            }}
          >
            {t('SAVE_FOR_LATER_LINK', { defaultValue: 'Save for Later' })}
          </ClickableComponent>
        </DropItem>
        <DropItem icon={<SvgDeleteBin />} iconSrc="/theme/icons/DeleteBin.svg">
          <DropConfirm
            component={component}
            variant={linkVariant}
            fullWidth={props.isXs}
            confirm={() => {
              onClick('delete');
              props.deleteItem();
            }}
          >
            {t('DELETE_BUTTON', { defaultValue: 'Delete' })}
          </DropConfirm>
        </DropItem>
        {props.canBook && (
          <>
            <P2GGrid item>
              <P2GDivider />
            </P2GGrid>
            <DropItem icon={<SvgCardPayment />} iconSrc="/theme/icons/CardPayment.svg">
              <P2GButton
                variant="primary"
                size={props.isXs ? 'large' : undefined}
                fullWidth={props.isXs}
                onClick={() => {
                  onClick('book');
                  props.book();
                }}
              >
                {t('BOOK_LINK', { defaultValue: 'Book' })}
              </P2GButton>
            </DropItem>
          </>
        )}
      </P2GGrid>
    </div>
  );
}
